import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation } from 'react-router-dom';
import SearchHighlightBanner from './components/SearchHighlightBanner';
import ReferencePagesBanner from './components/ReferencePagesBanner';
import ReferencePageBadge from './components/ReferencePageBadge';
import {
  isReferencePage,
  sortRowsWithReferenceFirst,
  REFERENCE_ROW_CLASS,
} from './utils/referencePages';
import Card from './components/Card';
import Table from './components/Table';
import Badge from './components/Badge';
import Tooltip from './components/Tooltip';
import ScorePill from './components/ScorePill';
import AuditEmptyState from './components/AuditEmptyState';
import { Eye, Info, CheckCircle, XCircle, AlertTriangle, Search } from 'lucide-react';
import decodeHtml from './utils/decodeHtml';
import { getSeoScoreColor } from './utils/scoreCalculations';
import { getScoreLabelText } from './utils/scoreLabels';
import { evaluateSeoCriteria, seoImpactPhrase } from './utils/seoCriteria';
import useSeoAudits from './hooks/useSeoAudits';
import useProjectScores from './hooks/useProjectScores';
import { useToast } from './ToastContext';

// ── Santé des critères (avant le tableau) ────────────────────────────────────

function CriteriaHealthSummary({ rows }) {
  const validRows = rows.filter(
    (r) => !r.loading && !r.error && Number(r.score_global) > 0
  );
  const n = validRows.length;
  if (!n) return null;

  const criteria = [
    { key: 'titleOk', label: 'Title', hint: '50–70 car.' },
    { key: 'metaOk', label: 'Meta desc.', hint: '120–170 car.' },
    { key: 'h1Ok', label: 'H1', hint: '≥ 1 balise H1' },
    { key: 'imagesOk', label: 'Images alt', hint: 'Tous les alt présents' },
    { key: 'canonOk', label: 'Canonical', hint: 'Balise présente' },
  ];

  return (
    <div className="mt-4 pt-4 border-t border-blue-gray-100">
      <p className="text-[11px] font-semibold text-blue-gray-400 uppercase tracking-wide mb-3">
        Santé des critères · {n} page{n > 1 ? 's' : ''} analysée{n > 1 ? 's' : ''}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        {criteria.map(({ key, label, hint }) => {
          const count = validRows.filter((r) => evaluateSeoCriteria(r)[key]).length;
          const pct = Math.round((count / n) * 100);
          const good = pct >= 80;
          const warn = pct >= 50;
          const barColor = good ? 'bg-green-500' : warn ? 'bg-amber-400' : 'bg-red-400';
          const textColor = good ? 'text-green-600' : warn ? 'text-amber-600' : 'text-red-500';
          return (
            <div key={key} className="bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-blue-gray-700">{label}</span>
                <span className={`text-xs font-bold ${textColor}`}>{pct}%</span>
              </div>
              <div className="w-full h-1.5 bg-blue-gray-100 rounded-full overflow-hidden mb-1.5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-[10px] text-blue-gray-400 leading-none">
                {count}/{n} · {hint}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Icône de critère ────────────────────────────────────────────────────────

function CriterionIcon({ ok, warning, tooltip }) {
  const icon = ok
    ? <CheckCircle size={16} className="text-green-500" />
    : warning
      ? <AlertTriangle size={16} className="text-amber-500" />
      : <XCircle size={16} className="text-red-400" />;
  return tooltip ? <Tooltip content={tooltip}>{icon}</Tooltip> : icon;
}

function SeoTableSkeleton() {
  const colWidths = ['w-[32%] min-w-[180px]', 'w-14', 'w-[52px]', 'w-[52px]', 'w-11', 'w-14', 'w-[52px]', 'w-[100px]'];
  return (
    <div className="w-full max-w-screen overflow-x-auto overflow-y-hidden rounded-xl border border-blue-gray-100 bg-white">
      <table className="min-w-[800px] w-full border-separate border-spacing-0">
        <thead>
          <tr className="bg-blue-gray-50/80 text-blue-gray-600">
            {colWidths.map((cw, i) => (
              <th key={i} scope="col" className={`px-3 py-2.5 ${cw}`}>
                <div className="h-3 bg-neutral-200 rounded animate-pulse max-w-[4rem]" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(8)].map((_, ri) => (
            <tr key={ri} className={`border-b border-blue-gray-100 last:border-b-0 ${ri % 2 === 0 ? 'bg-white' : 'bg-blue-gray-50/30'}`}>
              {colWidths.map((cw, ci) => (
                <td key={ci} className={`px-3 py-2.5 align-middle ${cw} ${ci === 0 ? 'min-w-0 max-w-[280px]' : ''}`}>
                  <div
                    className={`h-4 bg-neutral-100 rounded animate-pulse ${ci === 0 ? 'w-full max-w-[220px]' : 'w-8 mx-auto'}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProjectSEO() {
  const { project, refreshProject } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const { showToast } = useToast();
  const [tableDataVersion, setTableDataVersion] = useState(0);
  // Pré-remplir avec le terme de recherche venant du moteur de recherche audit
  const [searchQuery, setSearchQuery] = useState(location.state?.searchHighlight || '');
  const [referenceOnly, setReferenceOnly] = useState(false);

  const {
    sortedRows,
    seoRows,
    setSeoRows,
    seoLoading,
    seoError,
    progress,
    done,
    urls,
    sitemapLoading,
    auditsLoading,
    currentUrl,
    sort,
    rowLoading,
    handleAnalyseOne,
    handleAnalyseAll,
    handleSort,
    refetchSeoData
  } = useSeoAudits(
    project?.slug,
    project?.url,
    refreshProject,
    project?.reference_pages,
    project?.date_dernier_audit,
  );

  const validAudits = seoRows.filter(r => {
    const s = Number(r.score_global);
    return !Number.isNaN(s) && s > 0 && !r.loading && !r.error;
  });
  const { seo: averageScore } = useProjectScores(project, { seoRows });

  const filteredRows = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    let rows = sortedRows;
    if (q) {
      rows = rows.filter((row) => {
        const url = (row.url || '').toLowerCase().replace(/^https?:\/\//, '');
        const title = (row.title || '').toLowerCase();
        const meta = (row.meta_description || '').toLowerCase();
        return url.includes(q) || title.includes(q) || meta.includes(q);
      });
    }
    if (referenceOnly) {
      rows = rows.filter((row) => isReferencePage(row.url, project));
    }
    return sortRowsWithReferenceFirst(rows, project);
  }, [sortedRows, searchQuery, referenceOnly, project]);

  const { setSeoAnalyseButton } = useOutletContext();
  const handleAnalyseAllRef = useRef(handleAnalyseAll);
  handleAnalyseAllRef.current = handleAnalyseAll;

  useEffect(() => {
    setSeoAnalyseButton?.({
      onClick: () => handleAnalyseAllRef.current?.(),
      loading: seoLoading,
      disabled: sitemapLoading || !urls.length,
      label: 'Analyser les pages',
    });
    return () => setSeoAnalyseButton?.(null);
  }, [setSeoAnalyseButton, seoLoading, sitemapLoading, urls.length]);

  const isInitialLoading = sitemapLoading || auditsLoading;
  const noSitemapData = !isInitialLoading && urls.length === 0;
  const noSeoAuditsYet = urls.length > 0 && validAudits.length === 0 && !seoLoading;

  const getDetailPathForUrl = (pageUrl) => {
    if (!pageUrl) return null;
    let pageSlug = 'home';
    try {
      const u = new URL(pageUrl);
      let path = u.pathname.replace(/^\/+|\/+$/g, '');
      pageSlug = path ? path.replace(/\//g, '___') : 'home';
    } catch {
      pageSlug = encodeURIComponent(pageUrl);
    }
    return `/projects/${project.slug}/seo/${pageSlug}`;
  };

  return (
    <>
      <SearchHighlightBanner />
      <ReferencePagesBanner project={project} />
      {noSitemapData && (
        <AuditEmptyState
          className="mb-6"
          title="Aucune donnée SEO"
          description="Le sitemap n'a pas pu être chargé (site protégé par mot de passe ?) ou le projet n'a pas encore été audité. Lancez un audit complet pour récupérer le sitemap et générer les analyses SEO."
        />
      )}

      {validAudits.length > 0 && (
        <Card className="w-full">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-start gap-2 mb-1">
                <h2 className="font-heading text-base font-semibold text-blue-gray-900">Score SEO moyen</h2>
                <Tooltip content={
                  <div className="text-sm">
                    <div className="font-semibold mb-2">Calcul du score SEO</div>
                    <div className="space-y-1 text-neutral-200">
                      <div>• <strong>Title</strong> : longueur 50–70 car. (20%)</div>
                      <div>• <strong>Meta</strong> : longueur 120–170 car. (20%)</div>
                      <div>• <strong>H1</strong> : au moins un H1 présent (25%)</div>
                      <div>• <strong>Images</strong> : tous les attributs alt (15%)</div>
                      <div>• <strong>Canonical</strong> : balise présente (20%)</div>
                    </div>
                  </div>
                }>
                  <Info size={15} className="text-blue-gray-400 hover:text-blue-gray-600 cursor-help mt-0.5 flex-shrink-0" />
                </Tooltip>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-accent">{averageScore}</span>
                <span className="text-base text-blue-gray-400">/100</span>
              </div>
              <Badge color={getSeoScoreColor(averageScore)} className="text-sm">
                {getScoreLabelText(averageScore)}
              </Badge>
            </div>
          </div>
          <CriteriaHealthSummary rows={seoRows} />
        </Card>
      )}

      <Card className="w-full">
        {isInitialLoading ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="h-7 w-52 bg-neutral-200 rounded-lg animate-pulse" />
              <div className="h-10 w-full sm:w-64 bg-neutral-100 rounded-lg animate-pulse border border-neutral-200/50" />
            </div>
            <SeoTableSkeleton />
          </>
        ) : (
          <>
        {noSeoAuditsYet && (
          <div className="mb-4 p-3 rounded-lg bg-blue-gray-50 border border-blue-gray-200 text-sm text-blue-gray-700">
            Aucune analyse SEO pour l&apos;instant. Utilisez le bouton <strong>Analyser les pages</strong> (en haut) ou <button type="button" onClick={() => navigate(`/projects/${slug}/audit`)} className="text-accent font-medium underline hover:no-underline">lancez un audit complet</button>.
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="font-heading text-lg font-semibold text-blue-gray-900">Analyse SEO des URLs</h3>
          <div className="flex items-center gap-3 sm:ml-4">
            <label htmlFor="seo-search" className="sr-only">Rechercher dans les URLs</label>
            <div className="relative w-full sm:w-64 min-w-0">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-gray-400 pointer-events-none" />
              <input
                id="seo-search"
                type="search"
                placeholder="Rechercher…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm text-blue-gray-900 placeholder:text-blue-gray-400 bg-blue-gray-50/80 border border-blue-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-blue-gray-400 hover:text-blue-gray-600 hover:bg-blue-gray-200/60 transition-colors"
                  aria-label="Effacer la recherche"
                >
                  ×
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-xs text-blue-gray-500 shrink-0">
                {filteredRows.length} page{filteredRows.length !== 1 ? 's' : ''}
              </span>
            )}
            {(project?.reference_pages?.length > 0) && (
              <label className="inline-flex items-center gap-2 text-xs text-blue-gray-600 shrink-0 cursor-pointer">
                <input
                  type="checkbox"
                  checked={referenceOnly}
                  onChange={(e) => setReferenceOnly(e.target.checked)}
                  className="w-3.5 h-3.5 text-accent rounded border-blue-gray-300"
                />
                Réf. seulement
              </label>
            )}
          </div>
        </div>

        {seoLoading && (
          <div className="mb-6 p-4 rounded-xl bg-accent/5 border border-accent/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                <span className="text-blue-gray-900 font-semibold text-sm">Analyse en cours...</span>
              </div>
              <span className="text-accent font-bold">{progress}%</span>
            </div>
            {currentUrl && (
              <div className="text-blue-gray-500 font-mono text-xs truncate mb-2">{currentUrl}</div>
            )}
            <div className="w-full h-1.5 bg-blue-gray-100 rounded-full overflow-hidden">
              <div
                className="h-1.5 bg-accent rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {done && !seoLoading && (
          <div className="mb-6 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center gap-2">
            <CheckCircle size={16} className="text-green-600" />
            <span className="text-green-700 text-sm font-medium">Analyse terminée et scores recalculés.</span>
          </div>
        )}

          <Table
            key={`seo-audits-${tableDataVersion}`}
            rows={filteredRows}
            getRowClassName={(row) => (isReferencePage(row.url, project) ? REFERENCE_ROW_CLASS : '')}
            onRowClick={(row) => {
              const path = getDetailPathForUrl(row?.url);
              if (!path) return;
              navigate(path);
            }}
            columns={[
              { label: 'URL', key: 'url', align: 'left', className: 'w-[32%] min-w-[180px]' },
              { label: 'Score', key: 'score_global', align: 'center', className: 'w-[56px]' },
              { label: 'Title', key: 'title_check', align: 'center', className: 'w-[52px]' },
              { label: 'Meta', key: 'meta_check', align: 'center', className: 'w-[52px]' },
              { label: 'H1', key: 'h1_check', align: 'center', className: 'w-[44px]' },
              { label: 'Images', key: 'img_check', align: 'center', className: 'w-[56px]' },
              { label: 'Canon.', key: 'canon_check', align: 'center', className: 'w-[52px]' },
              { label: 'Impact business', key: 'impact', align: 'left', className: 'min-w-[200px] max-w-[280px]' },
              { label: '', key: 'detail', align: 'right', className: 'w-[100px]' },
            ]}
            caption="Analyse SEO des URLs"
            sort={sort}
            onSort={handleSort}
            renderCell={(page, col) => {
            const isGlobalAnalysisInProgress = seoLoading && currentUrl;
            const isCurrentUrlBeingAnalyzed = isGlobalAnalysisInProgress && page.url === currentUrl;
            const isUrlInQueue = isGlobalAnalysisInProgress && !page.score_global && !page.loading && !page.error;

            if (page.loading || isCurrentUrlBeingAnalyzed) {
              if (col.key === 'url') {
                return (
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-blue-gray-800 text-sm truncate">{page.url.replace(/^https?:\/\//, '')}</span>
                    <span className="inline-flex items-center gap-1 text-accent text-xs flex-shrink-0">
                      <span className="w-1 h-1 bg-accent rounded-full animate-pulse" />
                      {isCurrentUrlBeingAnalyzed ? 'Analyse...' : 'En cours'}
                    </span>
                  </span>
                );
              }
              return <span className="flex justify-center"><span className="w-2.5 h-2.5 border border-accent/30 border-t-accent rounded-full animate-spin" /></span>;
            }

            if (isUrlInQueue) {
              if (col.key === 'url') {
                return (
                  <span className="flex items-center gap-2 opacity-60 min-w-0">
                    <span className="text-blue-gray-600 text-sm truncate">{page.url.replace(/^https?:\/\//, '')}</span>
                    <span className="text-xs text-blue-gray-400 flex-shrink-0">En attente</span>
                  </span>
                );
              }
              return <span className="text-blue-gray-300 block">—</span>;
            }

            // --- Données depuis l'API (source de vérité) ---
            const {
              titleLen, metaLen, h1Count, nbImages, nbWithoutAlt,
              titleOk, metaOk, h1Ok, imagesOk, canonOk,
            } = evaluateSeoCriteria(page);

            if (col.key === 'url') {
              const hasScore = page.score_global !== undefined && Number(page.score_global) > 0;
              const isRef = isReferencePage(page.url, project);
              return (
                <span className="flex items-center gap-2 min-w-0">
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="hover:underline text-blue-gray-800 text-sm truncate font-normal"
                    title={page.url}
                  >
                    {page.url.replace(/^https?:\/\//, '')}
                  </a>
                  {isRef && <ReferencePageBadge compact />}
                  {hasScore
                    ? <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" title="Analysé" />
                    : <span className="w-1.5 h-1.5 bg-blue-gray-300 rounded-full flex-shrink-0" title="Non analysé" />}
                </span>
              );
            }

            if (col.key === 'title_check') {
              if (page.error) return <XCircle size={14} className="text-red-300 mx-auto" />;
              if (!page.title && titleLen === 0) return <div className="flex justify-center"><CriterionIcon ok={false} tooltip="Titre manquant" /></div>;
              const tip = `${titleLen} car. — ${titleOk ? 'OK' : titleLen < 50 ? 'Trop court (< 50)' : 'Trop long (> 70)'}`;
              return (
                <Tooltip content={<><div className="font-medium mb-1">{tip}</div><div className="text-white/80 text-sm truncate max-w-[240px]">{decodeHtml(page.title || '')}</div></>}>
                  <div className="flex justify-center">
                    <CriterionIcon ok={titleOk} warning={titleLen > 0} tooltip={null} />
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'meta_check') {
              if (page.error) return <XCircle size={14} className="text-red-300 mx-auto" />;
              if (!page.meta_description && metaLen === 0) return <div className="flex justify-center"><CriterionIcon ok={false} tooltip="Meta description manquante" /></div>;
              const tip = `${metaLen} car. — ${metaOk ? 'OK' : metaLen < 120 ? 'Trop courte (< 120)' : 'Trop longue (> 170)'}`;
              return (
                <Tooltip content={tip}>
                  <div className="flex justify-center">
                    <CriterionIcon ok={metaOk} warning={metaLen > 0} tooltip={null} />
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'h1_check') {
              if (page.error) return <XCircle size={14} className="text-red-300 mx-auto" />;
              const tip = h1Count === 0 ? 'H1 manquant' : h1Count === 1 ? `✓ ${page.h1 || ''}` : `${h1Count} H1 détectés (idéal : 1)`;
              return (
                <Tooltip content={tip}>
                  <div className="flex items-center justify-center gap-0.5">
                    <CriterionIcon ok={h1Ok} warning={h1Count > 1} tooltip={null} />
                    {h1Count > 1 && <span className="text-xs text-amber-600 font-semibold">×{h1Count}</span>}
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'img_check') {
              if (page.error) return <XCircle size={14} className="text-red-300 mx-auto" />;
              if (nbImages === 0) {
                return (
                  <Tooltip content="Aucune image (non pénalisant)">
                    <div className="flex justify-center"><span className="text-blue-gray-300 text-sm">—</span></div>
                  </Tooltip>
                );
              }
              const withAlt = nbImages - nbWithoutAlt;
              const tip = imagesOk ? `${nbImages} image${nbImages > 1 ? 's' : ''}, toutes avec alt` : `${nbWithoutAlt} sans alt sur ${nbImages}`;
              return (
                <Tooltip content={tip}>
                  <div className="flex items-center justify-center gap-1">
                    <CriterionIcon ok={imagesOk} warning={nbWithoutAlt < nbImages * 0.5} tooltip={null} />
                    {!imagesOk && <span className="text-xs text-amber-600">{withAlt}/{nbImages}</span>}
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'canon_check') {
              if (page.error) return <XCircle size={14} className="text-red-300 mx-auto" />;
              return (
                <Tooltip content={canonOk ? (page.canonical_url || 'Canonical présente') : 'Canonical manquante'}>
                  <div className="flex justify-center">
                    <CriterionIcon ok={canonOk} tooltip={null} />
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'impact') {
              if (page.error || page.loading || isUrlInQueue) return <span className="text-blue-gray-300 text-xs">—</span>;
              const c = evaluateSeoCriteria(page);
              const phrase = seoImpactPhrase(c);
              return (
                <span className="text-xs text-blue-gray-600 leading-snug line-clamp-3" title={phrase}>
                  {phrase}
                </span>
              );
            }

            if (col.key === 'score_global') {
              if (page.error) return <span className="text-red-400 text-sm">Erreur</span>;
              const score = Number(page.score_global);
              if (page.score_global === undefined || Number.isNaN(score)) return null;
              const detail = [
                `Title: ${Math.round(Number(page.score_title) || 0)}% (×20%)`,
                `Meta: ${Math.round(Number(page.score_meta) || 0)}% (×20%)`,
                `H1: ${Math.round(Number(page.score_h1) || 0)}% (×25%)`,
                `Images: ${Math.round(Number(page.score_images) || 0)}% (×15%)`,
                `Canonical: ${Math.round(Number(page.score_canonical) || 0)}% (×20%)`,
              ].join('\n');
              return (
                <Tooltip content={<pre className="text-sm whitespace-pre font-mono leading-relaxed">{detail}</pre>}>
                  <div className="flex justify-center">
                    <ScorePill value={score} />
                  </div>
                </Tooltip>
              );
            }

            if (col.key === 'detail') {
              if (!page.url) return null;
              if (rowLoading[page.url] || (page.loading && !page.error)) {
                return <span className="text-accent text-xs">Analyse...</span>;
              }
              const hasScore = page.score_global !== undefined && Number(page.score_global) > 0;
              const isDisabled = rowLoading[page.url] || seoLoading || !page.url;
              return (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isDisabled) handleAnalyseOne(page.url); }}
                    disabled={isDisabled}
                    className="text-sm text-blue-gray-500 hover:text-accent hover:bg-blue-gray-100 rounded px-1.5 py-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {hasScore ? 'Réanalyser' : 'Analyser'}
                  </button>
                  {hasScore && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const path = getDetailPathForUrl(page.url);
                        if (path) navigate(path);
                      }}
                      title="Voir le détail"
                      className="p-1 rounded text-blue-gray-400 hover:text-accent hover:bg-blue-gray-100 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            }
            return null;
            }}
          />
          </>
        )}
        {seoError && <div className="text-error mt-4">{seoError}</div>}
      </Card>
    </>
  );
}
